"use client"
import React, {
  ElementRef,
  createContext,
  forwardRef,
  useContext,
  useMemo,
} from "react"
import { IconProps, Verified } from "../../icons"
import { AddressDisplay, classNames, formatAddress } from "../../utils"
import { Avatar, AvatarImageProps } from "../Avatar"
import { FlexCenter, FlexCenterProps } from "../FlexCenter"

export type AccountLockupProps = {
  /**
   * Pass in a custom badge component if needed. Otherwise, renders a default badge when badge is present.
   */
  badge?: React.ReactNode

  /**
   * The address of the account
   */
  address: string

  /**
   * Whether to display the address in standard or compact form
   */
  addressDisplay?: AddressDisplay

  /**
   * This can either be the username of the account or an ens name for example. If provided, will take precedence over
   * the address
   */
  displayName?: string

  /**
   * The avatar image url of the account
   */
  imageUrl?: string

  /**
   * Decides whether or not to show the verified badge.
   */
  isVerified?: boolean | null

  /**
   * Supply children when you want to override the default Text behavior
   */
  children?: React.ReactNode

  /**
   * Pass in a custom avatar component if needed. Otherwise, renders a default avatar when imageUrl is present.
   */
  avatar?: React.ReactNode

  /**
   * Whether to display the avatar by default
   * NOTE: passing renderAvatar will ignore this value and always call renderAvatar()
   */
  withAvatar?: boolean
} & FlexCenterProps

const AccountLockupContext = createContext<
  Pick<
    AccountLockupProps,
    "address" | "addressDisplay" | "displayName" | "imageUrl" | "isVerified"
  >
>({
  address: "",
  addressDisplay: "standard",
  displayName: undefined,
  imageUrl: undefined,
  isVerified: false,
})

const AccountLockupBase = forwardRef<
  ElementRef<typeof FlexCenter>,
  AccountLockupProps
>(function AccountLockup(
  {
    children,
    className,
    address,
    addressDisplay,
    displayName,
    imageUrl,
    isVerified,
    badge,
    avatar,
    withAvatar,
    ...rest
  },
  ref,
) {
  const value = useMemo(
    () => ({
      address,
      addressDisplay,
      displayName,
      imageUrl,
      isVerified,
    }),
    [address, addressDisplay, displayName, imageUrl, isVerified],
  )

  const displayAvatar = useMemo(() => {
    if (avatar) {
      return avatar
    }

    if (withAvatar) {
      return <AccountLockupAvatar />
    }

    return null
  }, [avatar, withAvatar])

  return (
    <AccountLockupContext.Provider value={value}>
      <FlexCenter
        className={classNames("gap-2 text-sm", className)}
        {...rest}
        ref={ref}
      >
        {displayAvatar}
        {children ?? <AccountLockupText />}
        {badge ? badge : isVerified && <AccountLockupBadge />}
      </FlexCenter>
    </AccountLockupContext.Provider>
  )
})

const AccountLockupAvatar = ({
  className,
  size = 24,
  ...rest
}: Omit<AvatarImageProps, "src">) => {
  const { address, imageUrl } = useContext(AccountLockupContext)

  return (
    <Avatar
      className={className}
      rounded
      size={size}
      // if user has image, use it, otherwise use their address as seed for identicon
      {...(imageUrl ? { src: imageUrl } : { seed: address })}
      {...rest}
    />
  )
}

const AccountLockupText = () => {
  const { displayName, address, addressDisplay } =
    useContext(AccountLockupContext)

  return displayName
    ? displayName
    : formatAddress(address, { display: addressDisplay })
}

const AccountLockupBadge = ({
  className,
  size = 14,
  ...rest
}: IconProps & {
  innerFill?: IconProps["fill"]
}) => {
  return (
    <Verified
      className={classNames(className)}
      fill="text-primary"
      innerFill="transparent"
      size={size}
      {...rest}
    />
  )
}

export const AccountLockup = Object.assign(AccountLockupBase, {
  Avatar: AccountLockupAvatar,
  Text: AccountLockupText,
  Badge: AccountLockupBadge,
})
